import { ref, unref } from 'vue';
import { differenceInDays } from 'date-fns';
import { escapeSql } from '@rdrive/helper-sql';
import PaginatedReportGenerator, { reportTimeZone } from '@rdrive/helper-paginated-report';

const DEFAULT_LOGO_URL = 'https://paginated-report-service.rdrive.io/assets/logo.png';

/**
 * Owns everything behind the ItemsGrid "download report" dialog: the item
 * query, the search WHERE clause, row mapping, the report generator,
 * the PaginatedReportGenerator call and the GA event. The dialog itself only
 * collects the options.
 *
 * @param {object} deps
 * @param {object} deps.conn                  DuckDB connection
 * @param {object|import('vue').Ref} deps.settings   store settings
 *   (apiUrl, projectCode, projectName) — the ApiClient is stateless now, so
 *   settings is the only source for these
 * @param {string|import('vue').Ref} deps.logoUrl    project logo, falsy falls back to the service default
 * @param {(key: string, params?: object) => string} deps.t  i18n translator
 * @param {(name: string, params?: object) => void} [deps.trackEvent]
 */
export function useItemsGridReport({ conn, settings, logoUrl, t, trackEvent }) {
  const generating = ref(false);

  // apiUrl/projectCode used to be read from two places (settings for the row
  // URLs, the ApiClient's own copy for the report service). Both were written
  // together by the single `setValues` mutation and could never diverge in the
  // live app, and the ApiClient no longer carries them at all — settings is now
  // the one source.
  const cfg = () => unref(settings);

  const getCompanyURL = (clientId) => `${cfg().apiUrl}/${String(clientId).toLowerCase()}`;

  const generateItemURL = (itemId, subItemId) =>
    `${cfg().apiUrl}/projects/${cfg().projectCode}/items/${itemId}/sub-items/${subItemId}`;

  const getDaysSince = (dateString) => {
    if (!dateString) return 0;
    try {
      const statusDate = new Date(dateString);
      if (isNaN(statusDate.getTime())) return 0;
      const today = new Date();
      return differenceInDays(today, statusDate);
    } catch {
      return 0;
    }
  };

  const getDaysSinceText = (dateString) => {
    const daysSince = getDaysSince(dateString);
    if (daysSince === 0) return t('today');
    if (daysSince === 1) return t('dayAgo', { daysSince });
    if (daysSince > 1) return t('daysAgo', { daysSince });
    return '';
  };

  // Mirrors the search WHERE clause used by ItemsGrid so the report matches the
  // grid contents. NOTE the alias set here is `i.` — ItemsGrid's own copy uses
  // `fi.`; don't unify the two without remapping the aliases (R5).
  const getSearchWhereClause = (search) => {
    if (!search || !search.trim()) return '';
    const searchTerm = escapeSql(search.trim().toLowerCase());
    return `
                    WHERE (
                        LOWER(i.clientId) LIKE '%${searchTerm}%'
                        OR LOWER(it.title) LIKE '%${searchTerm}%'
                        OR LOWER(t2.name) LIKE '%${searchTerm}%'
                        OR LOWER(l.title) LIKE '%${searchTerm}%'
                        OR LOWER(ld.title) LIKE '%${searchTerm}%'
                        OR LOWER(st.title) LIKE '%${searchTerm}%'
                        OR LOWER(ct.name) LIKE '%${searchTerm}%'
                        OR strftime(TRY_CAST(i.statusCreatedDate AS TIMESTAMP), '%Y-%m-%d') LIKE '%${searchTerm}%'
                    )
                `;
  };

  const fetchReportRows = async ({ itemsView, search }) => {
    const itemsQuery = `SELECT
                        i.id,
                        i.clientId,
                        ft.title as subItem,
                        i.subItemId,
                        i.closedDate,
                        i.createdDate,
                        COALESCE(l.drawingId, '') as drawingId,
                        COALESCE(l.drawingTitle, '') as drawing,
                        COALESCE(l.title, '') as location,
                        COALESCE(l.id, '#') as locationId,
                        i.statusCreatedDate,
                        i.statusCreatedDate as updatedDate,
                        st.title as status,
                        it.title as template,
                        t1.name as controlledByTeam,
                        i.assigneeCreatedDate,
                        COALESCE(it.title, '') as profile,
                        t2.name as assigneeTeam,
                        it.categoryTitle as category,
                        it.subcategoryTitle as subCategory,
                        it.subCategoryId as subCategoryId,
                        COALESCE(st.color, '#FFE0E0') as color,
                        1 as sort,
                        i.type
                    FROM ${itemsView} i
                    LEFT JOIN item_templates it ON i.profileId = it.id
                    LEFT JOIN form_templates ft ON i.subItemTemplateId = ft.id
                    LEFT JOIN status_templates st ON i.statusId = st.id
                    LEFT JOIN locations l ON i.locationId = l.id
                    LEFT JOIN drawings ld ON l.drawingId = ld.id
                    LEFT JOIN teams t1 ON i.controlledByTeamId = t1.id
                    LEFT JOIN teams t2 ON i.assigneeTeamId = t2.id
                    LEFT JOIN teams ct ON i.createdByTeamId = ct.id
                    ${getSearchWhereClause(search)}
                `;

    const result = await unref(conn).query(itemsQuery);
    const formatDateToISO = (value) => {
      if (!value) return '';
      const date = new Date(value);
      return isNaN(date.getTime()) ? '' : date.toISOString();
    };

    return result.toArray().map(row => {
      const jsonRow = row.toJSON();
      return {
        ...jsonRow,
        companyURL: getCompanyURL(jsonRow.clientId),
        project: cfg().projectName,
        exchangeToken: '',
        url: generateItemURL(jsonRow.id, jsonRow.id),
        CONCAT: `${jsonRow.clientId}${jsonRow.id}`,
        subItem: jsonRow.subItem || '',
        closedDate: formatDateToISO(jsonRow.closedDate),
        createdDate: formatDateToISO(jsonRow.createdDate),
        statusCreatedDate: formatDateToISO(jsonRow.statusCreatedDate),
        updatedDate: formatDateToISO(jsonRow.updatedDate),
        updatedDateSubtext: getDaysSinceText(jsonRow.statusCreatedDate),
        assigneeCreatedDate: formatDateToISO(jsonRow.assigneeCreatedDate),
        status: (jsonRow.status || '').replace(/\n/g, ' ')
      };
    });
  };

  const downloadSummaryReport = async (reportName, fileFormat, reportType, rows) => {
    trackEvent?.('downloadReport', {
      action_type: 'button_click',
      apiUrl: cfg().apiUrl,
      project_code: cfg().projectCode,
      project_name: cfg().projectName,
      report_name: reportName,
      file_format: fileFormat,
      report_length: rows.length
    });

    const reportGenerator = new PaginatedReportGenerator();

    const resolvedLogoUrl = unref(logoUrl) ? unref(logoUrl) : DEFAULT_LOGO_URL;

    const reportData = {
      ItemsLatestData: rows,
      ProjectInfo: [
        {
          displayName: cfg().projectName,
          logoURL: resolvedLogoUrl,
          timeZone: reportTimeZone()
        }
      ],
      Translation: [
        {
          locale: "",
          subheader: t('summaryReport') + ' ' + reportType,
          category: t('category'),
          subcategory: t('subcategory'),
          controller: t('controllers'),
          drawing: t('drawings'),
          assignee: t('assignedTo'),
          location: t('location'),
          item: t('item'),
          subitem: t('subItem'),
          total: t('total'),
          updatedDate: t('updatedDate'),
          status: t('status'),
          footerReportGen: t('reportGenerated') + ': '
        }
      ]
    };

    await reportGenerator.generateReport({
      reportName: reportName,
      fileFormat: fileFormat,
      parameters: [
        {
          key: "CompanyURLParam",
          value: 'https://' + cfg().apiUrl
        },
        {
          key: "ProjectParam",
          value: cfg().projectCode
        }
      ],
      data: reportData
    });
  };

  /**
   * @param {object} options
   * @param {'grid'|'matrix'} options.mode
   * @param {string} options.reportType   matrix report type; ignored for 'grid'
   * @param {string} options.fileFormat
   * @param {string} options.pageSize     '' when the format has no page size
   * @param {string} options.itemsView
   * @param {string} options.search       the grid's DEBOUNCED search term (R5)
   */
  const generateReport = async ({ mode, reportType, fileFormat, pageSize, itemsView, search }) => {
    generating.value = true;
    try {
      const rows = await fetchReportRows({ itemsView, search });

      const pageSizeSuffix = pageSize ? ` - ${pageSize}` : ' - Excel';
      const reportName = mode === 'grid'
        ? `RDrive - Item Report${pageSizeSuffix}`
        : `RDrive - Summary Report (Count by Form) - ${reportType}${pageSizeSuffix}`;

      await downloadSummaryReport(
        reportName,
        fileFormat,
        mode === 'matrix' ? reportType : 'Grid',
        rows
      );
    } catch (error) {
      // Handle error
      console.error('Error generating report:', error);
    } finally {
      generating.value = false;
    }
  };

  return { generating, generateReport };
}
